import { Injectable, OnDestroy, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';

import { Observable, of, throwError, from, Subject } from 'rxjs';
import { take, switchMap, tap, catchError, map } from 'rxjs/operators';
import { ERROR_CODES } from '../../shared/constants/error_codes';
import { GameSession } from '../models/game.model';
import { GameSessionService } from '../services/game-session.service';
import { TranslationService } from '../services/translation.service';
import { ActiveSessionDialogComponent } from '../../features/playarea/shared/active-session-dialog/active-session-dialog.component';
import * as GameSessionActions from '../store/game-session/game-session.actions';
import { localizedURL } from './navigation.helper';
import { ConfirmDialogComponent } from '../../shared/components/confirm-dialog/confirm-dialog.component';


@Injectable({ providedIn: 'root' })
export class StartGameHelper implements OnDestroy {
  private readonly store = inject(
    Store<{ gameSession: { currentSession: GameSession | null } }>
  );

  // (kept for parity; no internal subscriptions remain)
  private destroy$ = new Subject<void>();

  private readonly currentSession$ = this.store.select(
    (s) => s.gameSession.currentSession
  );

  constructor(
    private gameSessionService: GameSessionService,
    private router: Router,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private translationService: TranslationService
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  startGame(gameId: number): Observable<'started' | 'cancelled'> {
    return this.currentSession$.pipe(
      take(1),
      // if there's a current session, ask; otherwise auto-confirm
      switchMap(current =>
        current
          ? this.dialog
              .open(ActiveSessionDialogComponent, {
                width: '400px',
                data: { currentSession: current },
              })
              .afterClosed()
              .pipe(take(1))
          : of(true)
      ),
      switchMap(confirmed => {
        // user cancelled -> normal completion with 'cancelled'
        if (!confirmed) return of<'cancelled'>('cancelled');

        // user confirmed -> create session and navigate, then 'started'
        return this.translationService.getCurrentLang().pipe(
          take(1),
          switchMap(lang =>
            this.gameSessionService.createGameSession(gameId, lang).pipe(
              tap(session =>
                this.store.dispatch(
                  GameSessionActions.setCurrentSession({ session })
                )
              ),
              switchMap(session =>
                from(
                  this.router.navigate([
                    localizedURL('/playarea/room', lang),
                    session.id,
                  ])
                ).pipe(map(() => 'started' as const))
              )
            )
          )
        );
      }),
      // still surface real errors (so caller can re-enable the button)
      catchError(err => {
        if (err?.error?.code === ERROR_CODES.INSUFFICIENT_TOKENS) {
          return this.translationService.translateBatch([
            'game_tokens.insufficient_tokens.title',
            'game_tokens.insufficient_tokens.play_message',
            'game_tokens.insufficient_tokens.confirmText',
            'game_tokens.insufficient_tokens.cancelText'
          ]).pipe(
            take(1),
            switchMap(([title, message, confirmText, cancelText]) => {
              const ref = this.dialog.open(ConfirmDialogComponent, {
                width: '400px',
                data: { title, message, confirmText, cancelText }
              });

              return ref.afterClosed().pipe(
                take(1),
                map(confirmed => {
                  if (confirmed) this.router.navigate(['/shop']);
                  return 'cancelled' as const;
                })
              );
            })
          );
        }
        this.snackBar.open(
          err?.error?.message || 'Failed to create game session',
          'Close',
          { duration: 5000 }
        );
        return throwError(() => err);
      })
    );
  }
}
